import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Navbar from '../components/Navbar';

const DrawingUpload = () => {
  const [file, setFile] = useState(null);
  const [drawingName, setDrawingName] = useState('');
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [fileError, setFileError] = useState('');
  const navigate = useNavigate();

    const ALLOWED_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpeg',
    'image/tiff'
  ];
  const ACCEPT_STRING = '.pdf,.png,.jpg,.jpeg,.tiff,.tif,image/png,image/jpeg,image/tiff,application/pdf';

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    setFileError('');
    
    // Validate file is a PDF
    if (selectedFile) {
      if (!ALLOWED_TYPES.includes(selectedFile.type)) {
        setFileError('Only PDF, PNG, JPG, JPEG, and TIFF files are allowed');
        setFile(null);
        return;
      }
      
      if (selectedFile.size > 10 * 1024 * 1024) { // 10MB max
        setFileError('File size should not exceed 10MB');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      
      // Set default drawing name from file name (without extension)
      const fileName = selectedFile.name.replace(/\.[^/.]+$/, "");
      setDrawingName(fileName);
    }
  };
  
  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    
    setFileError('');
    
    const droppedFile = e.dataTransfer.files[0];
    
    if (droppedFile) {
      if (droppedFile.type !== 'application/pdf') {
        setFileError('Only PDF files are allowed currently');
        return;
      }
      
      if (droppedFile.size > 10 * 1024 * 1024) { // 10MB max
        setFileError('File size should not exceed 10MB');
        return;
      }
      
      setFile(droppedFile);
      
      // Set default drawing name from file name (without extension)
      const fileName = droppedFile.name.replace(/\.[^/.]+$/, "");
      setDrawingName(fileName);
    }
  };
  
  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
  };
  
  // Handle file upload and automatic processing
  const handleUpload = async () => {
    if (!file) {
      setFileError('Please select a file to upload');
      return;
    }
    
    if (!drawingName.trim()) {
      setError('Please provide a name for your drawing');
      return;
    }
    
    setError('');
    setUploading(true);
    
    // Create form data
    const formData = new FormData();
    formData.append('drawing', file);
    formData.append('name', drawingName.trim());
    
    try {
      // Step 1: Upload the drawing
      const uploadRes = await api.post('/api/drawings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setUploading(false);
      setProcessing(true);

      // Get the drawing ID from the response
      const drawingId = uploadRes.data._id || uploadRes.data.documentName;

      if (drawingId) {
        // Processing is started by the backend after upload
        navigate('/dashboard', { 
          state: { 
            processingStarted: true, 
            drawingName: uploadRes.data.name,
            message: "Drawing uploaded and processing started" 
          }
        });
      }
   }
    catch (err) {
      setError(err.response?.data?.msg || 'Failed to upload drawing. Please try again.');
      setUploading(false);
      setProcessing(false);
    }
  };

  // Remove selected file
  const removeFile = () => {
    setFile(null);
    setDrawingName('');
    setFileError('');
    
    // Reset file input
    const fileInput = document.getElementById('file-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper
          elevation={3}
          sx={{ p: 4, borderRadius: 2 }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Upload P&ID Drawing
          </Typography>
          
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Upload a PDF drawing to start the information extraction process.
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {file ? (
            // File selected view
            <Box>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 1
                }}
              >
                <PictureAsPdfIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" noWrap>{file.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<CancelIcon sx={{color: 'grey.600'}}/>}
                  onClick={removeFile}
                  disabled={uploading || processing}
                >
                  Remove
                </Button>
              </Paper>
              
              <TextField
                fullWidth
                label="Drawing Name"
                variant="outlined"
                value={drawingName}
                onChange={(e) => setDrawingName(e.target.value)}
                sx={{ mb: 3 }}
                disabled={uploading || processing}
                required
              />
              
              <Box display="flex" justifyContent="space-between">
                {!(uploading || processing) && (
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/dashboard')}
                  >
                    Cancel
                  </Button>
                )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={uploading || processing || !drawingName.trim()}
              startIcon={
                uploading ? <CircularProgress size={20} color="inherit" /> :
                processing ? <CheckCircleIcon /> :
                null
              }
            >
              {uploading
                ? 'Uploading...'
                : processing
                  ? 'Starting processing...'
                  : 'Upload Drawing'}
            </Button>
              </Box>
            </Box>
          ) : (
            // File upload dropzone
            <Box
              sx={{
                border: '2px dashed',
                borderColor: fileError ? 'error.main' : 'primary.light',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: 'background.default',
                cursor: 'pointer'
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              // Modify this line - only click on the box itself, not on children
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  document.getElementById('file-upload').click();
                }
              }}
            >
              <input
                id="file-upload"
                type="file"
                accept={ACCEPT_STRING}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Drag & drop your P&ID file here
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                or click to browse files
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Maximum file size: 10MB
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                PDF, PNG, JPG, JPEG, TIFF formats supported
              </Typography>
              
              {fileError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {fileError}
                </Alert>
              )}
              
              {/* Add a Box container with display: flex to center the button */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<InsertDriveFileIcon />}
                  onClick={(e) => e.stopPropagation()}
                >
                  Select P&ID File
                  <input
                    type="file"
                    accept={ACCEPT_STRING}
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default DrawingUpload;